import { Component, Input, OnDestroy, TemplateRef, ViewChild, viewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators, AbstractControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ContentChange, QuillEditorComponent, QuillModules } from 'ngx-quill';
import Quill, { Delta } from 'quill/core';
import { EMPTY } from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';

import { transactions } from 'src/app/global.constants';
import { JobFinancialsActions } from 'src/app/job/job-financials/job-financials.actions';
import { DetailsHelperService } from 'src/app/services/details-helper.service';
import { FreyaNotificationsService } from 'src/app/services/freya-notifications.service';
import { SubSink } from 'subsink';

import { FullTransactionFragment, MutationUpdateTransactionArgs, UpdateTransactionGQL } from '../../../generated/graphql.generated';
import { safeParseJSON } from '../../js';
import { convertCentsToDollars } from '../../lib.ts/currency.util';
import { MutateObjectComponent, MutateObjectElement } from '../../shared/mutate-object/mutate-object.component';

// Requirement: only `stage` and `remarks` are editable — money already moved
// cannot be revised here. `updateTransaction` accepts neither an amount nor a
// payment type, and Edit is offered only while the transaction is `pending`.
@Component({
  selector: 'app-update-transaction',
  templateUrl: './update-transaction.component.html',
  styleUrls: ['./update-transaction.component.scss']
})

export class UpdateTransactionComponent implements OnDestroy {

  @ViewChild('mutate') mutateRef: MutateObjectComponent;

  @ViewChild('stage') stageRef: TemplateRef<any>;
  @ViewChild('remarks') remarksRef: TemplateRef<any>;

  @ViewChild('remarksReview') remarksReviewRef: TemplateRef<any>;

  protected readonly quillEditorWrapper = viewChild.required<QuillEditorComponent>('remarksWrapper');

  @Input() mutateType: 'update' | 'create';
  @Input() transaction: FullTransactionFragment;

  remainingCharCount = 350;

  steps: MutateObjectElement[];

  subs = new SubSink();

  transactionForm = new UntypedFormGroup({
    amount: new UntypedFormControl(undefined, [ Validators.required ]),
    customer: new UntypedFormControl('', [ Validators.required,]),
    job: new UntypedFormControl('', [ Validators.required,]),
    paymentType: new UntypedFormControl(undefined, [ Validators.required,]),
    stage: new UntypedFormControl('paid', [ Validators.required,]),
    type: new UntypedFormControl('payment'),
    invoice: new UntypedFormControl(undefined, [ Validators.required ]),
    sendReceipt: new UntypedFormControl(true),
    remarks: new UntypedFormControl(new Delta(), []),
  });

  remarksQuillModules: QuillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
    keyboard: {
      bindings: {

        enter: {
          key: 'Enter',
          handler: () => {
            return true;
          }
        }
      }
    }
  };
  remarksViewModules: QuillModules = {
    toolbar: false,
  };

  transactionStages = transactions.stages;

  open = false;

  maxRemarksLength = 350;

  constructor(
    private detailsHelper: DetailsHelperService,
    private localNotify: FreyaNotificationsService,
    private updateTransactionGQL: UpdateTransactionGQL,
    private store: Store,
  ) {

  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  mutateObject() {
    this.updateTransaction();
  }

  openDialog() {

    this.open = true;

    this.steps = [
      { name: 'Stage', ref: this.stageRef, control: 'stage', type: 'text' },
      { name: 'Remarks', ref: this.remarksRef, control: 'remarks', type: 'complex', reviewRef: this.remarksReviewRef },
    ];

    this.setFormValues();

    this.mutateRef.mutateType = this.mutateType;
    this.mutateRef.steps = this.steps;

    this.mutateRef.openDialog();
  }

  setFormValues() {
    this.transactionForm.reset({
      amount: convertCentsToDollars(this.transaction.amount),
      customer: this.transaction.customer,
      job: this.transaction.job,
      paymentType: this.transaction.paymentType.name,
      stage: this.transaction.stage,
      sendReceipt: Boolean(this.transaction.customer.email),
      invoice: this.transaction.invoice?.id,
      type: this.transaction.type,
      remarks: safeParseJSON(this.transaction.remarks, new Delta()),
    });
  }

  updateTransaction() {
    const updateTransactionInput = {
      transactionId: this.transaction.id,
      stage: this.transactionForm.value.stage,
      remarks: JSON.stringify(this.transactionForm.value.remarks),
    } as MutationUpdateTransactionArgs;

    this.subs.sink = this.updateTransactionGQL.mutate(updateTransactionInput).pipe(
      take(1),
      tap((res) => {
        this.detailsHelper.pushUpdate({
          id: this.transaction.id,
          type:'Transactions',
          action:'update'
        });

        this.closeDialog();
        this.localNotify.addToast.next({severity: 'success', summary: 'Transaction updated'});
        this.transaction = {
          ...this.transaction,
          stage: this.transactionForm.value.stage,
        }

        this.store.dispatch(JobFinancialsActions.updateTransactionSuccess({ data: res.data }));
      }),
      catchError((error) => {
        this.mutateRef.loading = false;
        this.localNotify.apolloError(`Failed to update transaction`,error);

        this.store.dispatch(JobFinancialsActions.updateTransactionError({ error }));

        return EMPTY;
      }),
    ).subscribe();
  }

  closeDialog() {
    this.open = false;
    this.mutateRef.closeDialog();
  }

  public setRemarksValidator(quill: Quill) {

    const maxValidator = (_control: AbstractControl): ValidationErrors | null => {

      const length = quill.getLength();

      if (length <= this.maxRemarksLength) { return null; }

      return {
        maxlength: {
          requiredLength: this.maxRemarksLength,
        },
      }

    }

    const remarksControl = this.transactionForm.get('remarks');

    remarksControl.setValidators([maxValidator]);

  }

  setRemainingCharCount(change: ContentChange) {

    const length = change.editor.getLength();

    this.remainingCharCount = this.maxRemarksLength - length;
  }

  public setupEditorEventPrevention(editor: any): void {
    if (!editor.container) return;

    const preventEnterBubbling = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };

    editor.container.addEventListener('keydown', preventEnterBubbling, true);
    editor.container.addEventListener('keyup', preventEnterBubbling, true);
  }

}
